//lib
import axios from 'axios'
import apiService from './api.service'
import { Notification, toast } from 'demo/components/ui'

// Several calls fail at once when a session dies (every listing, the notification
// count). One redirect, not one per call.
let sessionEndedHandled = false

function endDeadSession() {
    // Signed in as somebody else: the borrowed token is the one that died. Go back to
    // the account underneath, exactly as "Back to my account" does when the borrowed
    // token is gone (helper/impersonation.js). If that one is dead too, the next call
    // lands here again with nothing borrowed, and goes to sign in.
    const originalToken = localStorage.getItem('impersonator_token')
    if (originalToken) {
        localStorage.setItem('token', originalToken)
        localStorage.setItem(
            'username',
            localStorage.getItem('impersonator_username') || ''
        )
        localStorage.setItem('role', localStorage.getItem('impersonator_role') || '')
        localStorage.removeItem('impersonator_token')
        localStorage.removeItem('impersonator_username')
        localStorage.removeItem('impersonator_role')
        window.location.replace('/')
        return
    }

    // The same keys "Sign out" clears (UserDropdown.onSignOut).
    localStorage.removeItem('token')
    localStorage.removeItem('username')
    localStorage.removeItem('role')
    localStorage.removeItem('user_id')
    // Read by the sign-in page, so it can say why the person is looking at it.
    sessionStorage.setItem('session_ended', '1')

    // Back to the page they were on once they sign in again.
    const here = window.location.pathname + window.location.search
    window.location.replace(
        '/sign-in' +
            (here && here !== '/' && !here.startsWith('/sign-in')
                ? '?redirectUrl=' + encodeURIComponent(here)
                : '')
    )
}

function handleErrorResponse(error) {
    const authError = error.response && error.response?.status === 401
    const versionUpdateError = error.response && error.response?.status === 410
    const manyAttemptsError = error.response && error.response?.status === 429
    const unexpectedError = error.response && error.response?.status === 500
    // const maintainanceError = error.response && error.response.status === 503;
    // const networkError = !error.response;

    // const merchantName = localStorage.getItem('merchant_name');

    // // handle no network
    // if (networkError) {
    //   toast.error('Server is busy right now. Please refresh and try again later.');
    // }

    //handle 401
    //
    // Only a dead session is sent to sign in. The API answers 401 for two things: a
    // token it no longer accepts ("Unauthenticated" - expired, revoked, account
    // switched off) and a role refused by a route guard ("This action is
    // unauthorized." - still signed in). Treating both as sign-out would throw an
    // operation user out for opening a superadmin screen. Before this, neither did
    // anything: the calls failed in the network tab and the screen just stayed empty.
    if (
        authError &&
        error.response?.data?.message === 'Unauthenticated' &&
        localStorage.getItem('token') &&
        !sessionEndedHandled
    ) {
        sessionEndedHandled = true
        endDeadSession()
    }

    //   setTimeout(() => {
    //     window.location = '/';
    //   }, 0);
    // }

    // handle 410: this bundle is older than the API expects.
    //
    // reload(true) was the whole strategy, and modern Chrome IGNORES the force
    // flag - a tab whose index.html was cached reloaded straight back onto the
    // same old bundle, hit 410 again, and looped forever, hammering the API on
    // every mount (the notification-count spam of 10 Sep). Two changes: the
    // reload cache-busts through a query param, and after two failed attempts
    // it STOPS and tells the person instead of spamming.
    if (versionUpdateError) {
        const attempts = Number(
            sessionStorage.getItem('version_reload_attempts') || 0
        )

        if (attempts >= 2) {
            toast.push(
                <Notification type="danger" title="Update could not load">
                    This tab is stuck on an old version. Please close it and
                    open the portal again.
                </Notification>
            )
        } else {
            sessionStorage.setItem(
                'version_reload_attempts',
                String(attempts + 1)
            )
            toast.push(
                <Notification type="info" title="New version available">
                    The portal has been updated. Reloading to the latest
                    version...
                </Notification>
            )
            setTimeout(() => {
                const url = new URL(window.location.href)
                url.searchParams.set(
                    'v',
                    String(attempts + 1) +
                        '-' +
                        Math.random().toString(36).slice(2)
                )
                window.location.replace(url.toString())
            }, 1500)
        }
    }

    // handle 429
    if (manyAttemptsError) {
        toast.push(
            <Notification type="danger" title="Error">
                Too many attempts, please try again later
            </Notification>
        )
    }

    // handle 500
    if (unexpectedError) {
        toast.push(
            <Notification type="danger" title="Error">
                Oops! Something went wrong unexpectedly. Please contact support
                for assistance.
            </Notification>
        )
    }

    // // handle 503
    // if (maintainanceError) {
    //   window.location = `?open=under_maintenance`;
    // }

    return Promise.reject(error)
}

function initializeInterceptors() {
    axios.interceptors.response.use(
        (response) => {
            // A successful call means the gate passed - this tab is current, so
            // the reload-attempt guard starts fresh for the next deploy.
            sessionStorage.removeItem('version_reload_attempts')
            return response
        },
        (error) => {
            return handleErrorResponse(error)
        }
    )
}

axios.interceptors.request.use(
    (config) => config,
    (error) => {
        return Promise.reject(error)
    }
)

function setToken(token) {
    axios.defaults.headers.common = {
        Authorization: `Bearer ${token}`,
        Accept: `application/json`,
        // The build states its version on every request; the API refuses a stale
        // bundle with 410 once the gate is armed (see handleErrorResponse).
        'Admin-Version': process.env.REACT_APP_VERSION,
    }
}

const httpService = {
    get: axios.get,
    patch: axios.patch,
    post: axios.post,
    put: axios.put,
    delete: axios.delete,
    setToken,
    initializeInterceptors,
}

export default httpService
